import React, { useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import { createColumnHelper } from '@tanstack/react-table'
import { generatePath } from 'react-router-dom'

import { useIsMobile } from '@/hooks'
import { TableComponent } from '@/components/table'
import { TemplatePayment } from '@/types.ts'
import { APP_ROUTES } from '@/constants.ts'
import { FetchErrorMessage } from '@/components'
import { router } from '@/router.tsx'
import { DefaultFiltersState, TableSort } from '@/stores'
import { useGetTemplatePayments } from '@/services'
import { useAppDispatch, useAppSelector } from '@/stores/hooks.ts'
import { userSelectors } from '@/stores/slices/user-slice.ts'
import { templatesActions, templatesSelectors } from '@/stores/slices/templates-slice.ts'

import {
  defaultSettings,
  getColumnsSettings,
  getStaticColumns,
  KEY_PAGE,
  PartialTemplates,
} from './default-settings.tsx'

const columnHelper = createColumnHelper<TemplatePayment>()
const defaultColumns = getColumnsSettings(columnHelper)
const staticColumns = getStaticColumns(columnHelper)

export const TemplatePaymentsTable = (): React.ReactElement => {
  const settings = useAppSelector((state) => userSelectors.personalSettings(state, KEY_PAGE))
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()
  const { data, isLoading, isFetching, isError, refetch } = useGetTemplatePayments()

  const { page, showCount, sort } = useAppSelector(templatesSelectors.filters)

  const handlePaginationParam = (param: keyof DefaultFiltersState) => (value: number) =>
    dispatch(
      templatesActions.setFilter({
        filterKey: param,
        filterValue: value,
      })
    )
  const handleSort = (nextSort: TableSort) =>
    dispatch(
      templatesActions.setFilter({
        filterKey: 'sort',
        filterValue: nextSort,
      })
    )

  const handleViewTransaction = useCallback(
    (row: TemplatePayment) => () => {
      router.navigate(generatePath(APP_ROUTES.TemplatePaymentView, { id: row.id.toString() }))
    },
    []
  )

  const templatesPaymentCols = useMemo(() => {
    const format = isMobile ? 'mobile' : 'desktop'

    const desktopSettings = (settings?.[format] ?? Object.keys(defaultSettings)) as Array<keyof PartialTemplates>

    const dynamicSettings = desktopSettings.map((e) => defaultColumns[e])
    return [...dynamicSettings, ...Object.values(staticColumns)]
  }, [settings, isMobile])

  return (
    <FetchErrorMessage isError={isError} refetch={refetch} isFetching={isFetching}>
      <Box mt={3}>
        <TableComponent<TemplatePayment>
          rowKey="id"
          columns={templatesPaymentCols}
          page={page}
          isLoading={isLoading || isFetching}
          columnPinning={{ right: ['template-actions'] }}
          sort={sort}
          size={showCount}
          data={data?.data?.content ?? []}
          totalPages={data?.data?.totalPages}
          onPageChange={handlePaginationParam('page')}
          onRowClick={handleViewTransaction}
          onSizeChange={handlePaginationParam('showCount')}
          onSort={handleSort}
        />
      </Box>
    </FetchErrorMessage>
  )
}
